import { EquatorialCoord } from "@hscmap/angle";
import { vec3 } from "gl-matrix";
import { PointerPicker, PointerPickerEvent } from "@/stellar-globe-components/PointerPicker";
import { FrameStore } from "@/modules/base";
import { CatalogOverplotModule, IRowRef } from "@/modules/CatalogOverplotModule";
import { MarkerStyle } from "@/modules/CatalogOverplotModule/Marker/marker";
import { Frame, store } from "@/store";
import { ad2xyz } from '@/utils/math';
import { CreateElement } from "vue";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Marker from '../Marker';

interface ICandidate extends IRowRef {
  distance: number,
}

@Component
export default class ObjectPickerComponent extends Vue {
  @Prop({ required: true })
  private frameStore!: FrameStore<typeof CatalogOverplotModule>

  @Prop({ required: true })
  private frame!: Frame

  private rowRef: IRowRef | null = null
  @Watch('rowRef')
  onRowRefChange() {
    this.frameStore.$emit('hover-object', this.rowRef)
  }

  private mousemove(e: PointerPickerEvent) {
    this.rowRef = this.pick(e.coord)
  }

  private pick(coord: EquatorialCoord): IRowRef | null {
    const r = this.searchRadius()
    const cands: ICandidate[] = []
    for (const co of this.frameStore.cos) {
      if (co.show) {
        for (const i of co.spatialIndex.nearest(coord.xyz, 1, r)) {
          const [a, d] = co.coord3[i]
          const distance = vec3.sqrDist(coord.xyz, ad2xyz(a, d))
          cands.push({ co, i, distance })
        }
      }
    }
    if (cands.length > 0) {
      const { co, i } = cands.sort((c1, c2) => c1.distance - c2.distance)[0]
      return { co, i }
    }
    return null
  }

  private searchRadius() {
    const globe = store.globe(this.frame)
    const { fovy } = globe.viewFactory
    const R = 96
    const H = globe.canvas.domElement.clientHeight
    return fovy * R / H
  }

  get markerCoords() {
    if (this.rowRef) {
      const { co, i } = this.rowRef
      return [co.coord3[i]]
    } else {
      return []
    }
  }

  render(h: CreateElement) {
    return h('div', [
      h(PointerPicker, {
        on: { mousemove: (e: PointerPickerEvent) => this.mousemove(e) },
      }),
      h(Marker, {
        props: {
          coord3: this.markerCoords,
          color: [1, 0, 0, 0.75],
          marker: MarkerStyle.CIRCLE,
        },
      }),
    ])
  }
}
